// Durable client-side activity log — the record of what the app actually did,
// written from the browser and shipped to a sink that is NOT our own API.
//
// WHY THIS EXISTS
// ---------------
// The failure we could never explain: services are down (they come back a few
// minutes later on a cron), so a login's requests never land on the server.
// Nothing reaches the backend, therefore the backend's activity log has nothing
// to say — the outage is invisible in exactly the logs we would go and read.
// Meanwhile the user watches a spinner, the retry ladder in common/api runs out,
// and they end up on the recovery screen with no trace left behind:
// `console.*` is stubbed out in production (see App.tsx), so a console line is
// nowhere, and a bare `catch {}` is nothing at all.
//
// So the log has to be written where the failure happens — in the browser — and
// it has to survive the outage. Two rules follow, and they are the whole design:
//
//   1. WRITE LOCALLY FIRST, SYNCHRONOUSLY. Every entry lands in localStorage the
//      instant it is created. That is deliberately not IndexedDB: an IDB write is
//      async and loses the race against `window.location.replace('/login')` (see
//      handleSessionExpired) or a user closing a tab that has been spinning for
//      two minutes — which is precisely the entry we most want to keep.
//   2. SHIP TO A SINK THAT IS UP WHEN THE API IS DOWN. The queue drains to a
//      standalone Lambda Function URL (backend/frontend_log_lambda.py), which has
//      no dependency on the ECS services the cron powers on. Anything that fails
//      to send stays queued and goes out on the next flush — including on the
//      NEXT PAGE LOAD, hours later if need be, because the queue is persistent.
//      An outage can therefore never silently eat its own evidence.
//
// The sink partitions by date and user, so an incident is `dt=2026-07-29/
// user=142/` — see the deploy guide for how to read a day back.
//
// This module owns no UI and never throws. Logging is diagnostics: it must not
// be able to break the thing it is observing, so every entry point is wrapped
// and failures inside the logger are dropped on the floor by design.

export type LogLevel = 'info' | 'warn' | 'error';

const LEVEL_RANK: Record<LogLevel, number> = { info: 10, warn: 20, error: 30 };

/**
 * Only failures are recorded. Anything below this level is discarded at the point
 * of logging — never buffered, never sent, never stored.
 *
 * This log exists to explain a broken login or a crashed screen, not to trace
 * healthy usage: a full activity trail buried the handful of entries that matter
 * in page loads and successful sign-ins, and burned the browser's 400-entry buffer
 * on noise, so a long session could evict the very failure someone was chasing.
 *
 * `warn` rather than `error` because for a REQUEST the warnings are the failure
 * story — the stalled call, each rung of the retry ladder, the session being
 * rejected. An `api_failed` with no retries above it says far less about a gateway
 * outage. See MIN_LEVEL_OTHER for everything that isn't a request.
 *
 * Set to 'error' for failures only; 'info' restores the full trail.
 */
const MIN_LEVEL: LogLevel = 'warn';

/**
 * Non-API events that are recorded — an explicit list, not a severity bar.
 *
 * The rule is "the user SAW something go wrong", not "an error object existed".
 * Level turned out to be the wrong test: `caught_error` fires from all ~34
 * `captureError` sites, and a catch block that handled its error and carried on
 * has broken nothing — the user never knew. Those entries were the bulk of
 * `Other/` and none of them explained a visible failure, which is the only
 * question this log exists to answer.
 *
 * So each of these corresponds to something on screen:
 *
 *   react_crash          the recovery screen replaced the app
 *   error_screen_shown   the error screen replaced a section
 *   connection_lost      the red connectivity strip is up
 *   service_unavailable  the same strip, for services answering 5xx
 *
 * The two connection events are here on purpose even though they do not break the
 * view: they are a visible failure notice, and they are the case this whole log was
 * built for — a login whose requests never land, which the server cannot record
 * because nothing reaches it. Dropping them would leave an outage with no trace at
 * all on either side.
 *
 * `url` is the discriminator for API-vs-not, deliberately the same one the sink
 * uses, so what this does and what appears in S3 cannot drift apart.
 *
 * An allow-list rather than a deny-list because the failure modes point opposite
 * ways: forgetting to add a new event costs one missing line, forgetting to exclude
 * one floods every user's file. Add to it when a new event genuinely marks a
 * visible failure.
 */
const OTHER_EVENTS = new Set([
  'react_crash',
  'error_screen_shown',
  'connection_lost',
  'service_unavailable'
]);

export type LogEntry = {
  /** Unique per entry — the sink appends blindly, so de-dupe on this when reading. */
  id: string;
  /** When it happened, client clock, ISO-8601. The sink records its own receipt time too. */
  ts: string;
  lvl: LogLevel;
  /** Machine-readable name of WHAT happened, e.g. `api_failed`, `connection_lost`. */
  event: string;
  /** One line of prose for a human skimming the file. */
  msg?: string;
  /** WHY, in the classification the code actually used (e.g. `no_response`). */
  reason?: string;

  // ── Which API / which request ──────────────────────────────────────────────
  url?: string;
  method?: string;
  /** HTTP status, or null when there was no response at all (the outage case). */
  status?: number | null;
  /** Axios/browser error code, e.g. ECONNABORTED, ERR_NETWORK. */
  code?: string;
  /** Correlates the stall, the retries and the final failure of ONE request. */
  reqId?: string;
  /** 1-based attempt number for retried requests. */
  attempt?: number;
  /** Wall time from request start, ms — how long the user actually waited. */
  durationMs?: number;

  // ── Correlation ────────────────────────────────────────────────────────────
  /** Groups every entry produced during one outage window. */
  outageId?: string;
  /** Groups every entry from one page load. */
  sessionId?: string;

  // ── Who / where / which build ──────────────────────────────────────────────
  userId?: string | null;
  /**
   * From localStorage. Appended to the S3 folder so it names a person rather than
   * a number — `userid=142_Shaheryar` instead of `userid=142`.
   */
  username?: string | null;
  email?: string | null;
  role?: string | null;
  appVersion?: string | null;
  buildVersion?: string;
  route?: string;
  /** Connection state as common/connection saw it at the time. */
  conn?: string;
  /** navigator.onLine — kept because it so often disagrees with reality. */
  online?: boolean;
  /** 'visible' | 'hidden' — a stalled request in a hidden tab is a different bug. */
  vis?: string;
  /** IANA zone, e.g. `Asia/Karachi`. Names a city, so it reads as a location. */
  tz?: string;
  /** Minutes ahead of UTC, e.g. 300 for UTC+5 — a cross-check on `tz`. */
  tzOffset?: number;
  /** BCP-47 tag, e.g. `en-PK`; the region subtag is the nearest thing to a country. */
  locale?: string;
  ua?: string;

  /** Anything event-specific. Scrubbed of credentials before it is stored. */
  extra?: Record<string, any>;
};

// ── Configuration ────────────────────────────────────────────────────────────
// CRA inlines REACT_APP_* at build time.
//   REACT_APP_FE_LOG_URL - Lambda Function URL that receives batches.
//   REACT_APP_FE_LOG_KEY - shared string echoed as ?k=; a speed bump against
//                          drive-by posting to a public endpoint, NOT a secret
//                          (it ships in the bundle). See the deploy guide.
const LOG_URL = process.env.REACT_APP_FE_LOG_URL || '';
const LOG_KEY = process.env.REACT_APP_FE_LOG_KEY || '';

/** With no endpoint configured we still buffer locally, so /status/diagnostics works. */
export const remoteLoggingEnabled = Boolean(LOG_URL);

/**
 * localStorage keys. The `__felog` prefix is preserved by clearAppStorage — a
 * logout or session-expiry wipe must not destroy the log of what caused it,
 * which is the single most valuable entry we ever write.
 */
const QUEUE_KEY = '__felog_queue';
const META_KEY = '__felog_meta';

/** Ring-buffer caps. Entries are ~400 bytes, so this is a few hundred KB worst case. */
const MAX_ENTRIES = 400;
const MAX_QUEUE_BYTES = 256 * 1024;
/**
 * Per-batch cap. Kept under 64KB because `fetch(keepalive)` and `sendBeacon`
 * both refuse larger bodies — the unload path is exactly when we can least
 * afford a silent rejection.
 */
const MAX_BATCH_ENTRIES = 40;
const MAX_BATCH_BYTES = 48 * 1024;
/** Truncate long strings (stacks, messages) so one entry can't eat the buffer. */
const MAX_FIELD_CHARS = 4000;

/** Regular drain while anything is queued. */
const FLUSH_INTERVAL_MS = 15000;
/** An error-level entry ships almost immediately, but batches a burst first. */
const ERROR_FLUSH_DEBOUNCE_MS = 1500;
/** Backoff after a failed send. The sink being unreachable must not cause a storm. */
const SEND_BACKOFF_MS = [5000, 15000, 45000, 120000];

// ── Per-page-load context ────────────────────────────────────────────────────
const rand = () => Math.random().toString(36).slice(2, 8);
const sessionId = `s_${Date.now().toString(36)}_${rand()}`;
let seq = 0;

const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
/**
 * Where and on what, as far as the browser will say. All read locally — nothing
 * here makes a request, which matters because this module has to keep working
 * during exactly the outages it records.
 *
 * `tz` and `locale` are the only geography available: a Lambda Function URL gets
 * no country header, and an IP lookup would mean an external call on every batch.
 * They are what the machine CLAIMS, not verified location — a VPN or a
 * misconfigured clock will misreport, so the sink labels them "inferred".
 */
let tz = '';
let tzOffset: number | undefined;
let locale = '';
try {
  tz = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
  // Minutes WEST of UTC as JS reports it, negated so it reads the conventional
  // way round: Asia/Karachi is UTC+5, so +300 rather than -300.
  tzOffset = -new Date().getTimezoneOffset();
} catch {
  /* Intl unavailable — the timestamp is still absolute, so this is cosmetic. */
}
try {
  // e.g. "en-PK" — the region subtag is the closest thing to a country we get.
  locale = navigator.language || (navigator as any).userLanguage || '';
} catch {
  /* ignore */
}

/**
 * Mirrors common/connection's state. Pushed in rather than imported so this
 * module has no dependency on connection.ts (which imports THIS one), keeping
 * the import graph acyclic.
 */
let currentConn = 'ok';
export function setLogConnectionState(state: string): void {
  currentConn = state;
}

/** Set from the login response / identifyFromStorage so entries carry a person. */
let userOverride: {
  userId?: string | null;
  username?: string | null;
  email?: string | null;
  role?: string | null;
} = {};
export function setLogUser(user: {
  userId?: string | null;
  username?: string | null;
  email?: string | null;
  role?: string | null;
}): void {
  userOverride = { ...userOverride, ...user };
}

/**
 * Forget who was signed in. Call this wherever the session is wiped.
 *
 * This is in-memory module state, so it does NOT go away when localStorage does —
 * and every entry prefers it over storage, which is the whole point during login
 * (the email is known before there is anything to read back). The consequence was
 * that signing out and opening the password-reset page within the same SPA
 * navigation filed a stranger's reset attempt under `userid=2_someone@example.com`:
 * the page had cleared storage at import, but the log still remembered.
 *
 * Attribution is the one thing this log cannot afford to get wrong, so the
 * identity has to be cleared as deliberately as it is set.
 */
export function clearLogUser(): void {
  userOverride = {};
}

// ── Outage correlation ───────────────────────────────────────────────────────
// One id per outage window, stamped on everything logged while it is open, so
// the whole incident reads as a single story instead of scattered failures.
let outage: { id: string; startedAt: number; failures: number } | null = null;

/** Called by common/connection when it concludes the API is unreachable. */
export function beginOutage(): string {
  if (!outage) {
    outage = { id: `o_${Date.now().toString(36)}_${rand()}`, startedAt: Date.now(), failures: 0 };
  }
  return outage.id;
}

/** Called when the API answers again. Returns the closed window for logging. */
export function endOutage(): { id: string; durationMs: number; failures: number } | null {
  if (!outage) return null;
  const closed = {
    id: outage.id,
    durationMs: Date.now() - outage.startedAt,
    failures: outage.failures
  };
  outage = null;
  return closed;
}

export function getOutageId(): string | undefined {
  return outage?.id;
}

/** Count request-level failures against the open window, for the summary entry. */
export function noteOutageFailure(): void {
  if (outage) outage.failures += 1;
}

// ── Redaction ────────────────────────────────────────────────────────────────
// Credentials must never reach the log. common/api already keeps `config.params`
// (which carries token + userId) out of its failure metadata; this is the second
// line of defence for anything a caller passes in `extra`.
// `session` is deliberately NOT in here: it would redact our own `sessionId`
// correlation field, and the thing it was meant to catch — `sessionToken` — is
// already matched by `token`.
const SECRET_KEY = /(token|password|secret|accesskey|authorization|credential|apikey|jwt|cookie)/i;

const scrub = (value: any, depth = 0): any => {
  if (value == null) return value;
  if (typeof value === 'string') {
    return value.length > MAX_FIELD_CHARS ? `${value.slice(0, MAX_FIELD_CHARS)}…[truncated]` : value;
  }
  if (typeof value === 'number' || typeof value === 'boolean') return value;
  if (depth >= 3) return '[depth]';
  if (Array.isArray(value)) return value.slice(0, 20).map((v) => scrub(v, depth + 1));
  if (typeof value === 'object') {
    const out: Record<string, any> = {};
    Object.keys(value)
      .slice(0, 30)
      .forEach((key) => {
        out[key] = SECRET_KEY.test(key) ? '[redacted]' : scrub(value[key], depth + 1);
      });
    return out;
  }
  // Functions, symbols, bigints: describe rather than serialize.
  return String(value).slice(0, 200);
};

// ── Queue persistence ────────────────────────────────────────────────────────
const readQueue = (): LogEntry[] => {
  try {
    const raw = localStorage.getItem(QUEUE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    // Corrupt or unreadable: start clean rather than fail every future write.
    try {
      localStorage.removeItem(QUEUE_KEY);
    } catch {
      /* nothing more we can do */
    }
    return [];
  }
};

/** How many entries we had to discard, so a truncated file says so out loud. */
const bumpDropped = (n: number) => {
  try {
    const meta = JSON.parse(localStorage.getItem(META_KEY) || '{}');
    meta.dropped = (Number(meta.dropped) || 0) + n;
    localStorage.setItem(META_KEY, JSON.stringify(meta));
  } catch {
    /* metadata is a nicety, never worth throwing over */
  }
};

const peekDropped = (): number => {
  try {
    return Number(JSON.parse(localStorage.getItem(META_KEY) || '{}').dropped) || 0;
  } catch {
    return 0;
  }
};

/**
 * Subtract only what a confirmed batch actually reported. Clearing the counter
 * outright would lose entries discarded between reading it and the send landing,
 * and clearing it before the send would lose the fact entirely if the send failed.
 */
const settleDropped = (reported: number): void => {
  if (!reported) return;
  try {
    const meta = JSON.parse(localStorage.getItem(META_KEY) || '{}');
    const remaining = Math.max(0, (Number(meta.dropped) || 0) - reported);
    if (remaining) meta.dropped = remaining;
    else delete meta.dropped;
    localStorage.setItem(META_KEY, JSON.stringify(meta));
  } catch {
    /* metadata only */
  }
};

const writeQueue = (entries: LogEntry[]): void => {
  let queue = entries;
  // Oldest-first eviction: during a long outage the newest entries describe the
  // state the user is actually stuck in.
  if (queue.length > MAX_ENTRIES) {
    bumpDropped(queue.length - MAX_ENTRIES);
    queue = queue.slice(-MAX_ENTRIES);
  }
  let payload = JSON.stringify(queue);
  while (payload.length > MAX_QUEUE_BYTES && queue.length > 1) {
    const drop = Math.max(1, Math.ceil(queue.length * 0.2));
    bumpDropped(drop);
    queue = queue.slice(drop);
    payload = JSON.stringify(queue);
  }
  try {
    localStorage.setItem(QUEUE_KEY, payload);
  } catch {
    // Quota exceeded (or storage disabled). Halve and retry once; if that fails
    // too, drop this write — a full disk must not break the app.
    try {
      const half = queue.slice(Math.ceil(queue.length / 2));
      bumpDropped(queue.length - half.length);
      localStorage.setItem(QUEUE_KEY, JSON.stringify(half));
    } catch {
      /* give up on this entry */
    }
  }
};

// ── Writing an entry ─────────────────────────────────────────────────────────
export function logEvent(
  level: LogLevel,
  event: string,
  props?: Partial<LogEntry> & { extra?: Record<string, any> }
): void {
  // Dropped here, before anything is written: the cheapest place to say no, and it
  // guarantees an informational event costs nothing at all — no storage write, no
  // buffer slot, no bytes on the wire.
  // A request-scoped entry is judged by severity (the retry ladder is the story);
  // everything else has to be one of the events that marks a visible failure.
  if (props?.url) {
    if (LEVEL_RANK[level] < LEVEL_RANK[MIN_LEVEL]) return;
  } else if (!OTHER_EVENTS.has(event)) {
    return;
  }
  try {
    const { extra, ...rest } = props || {};
    const entry: LogEntry = {
      id: `${sessionId}-${++seq}`,
      ts: new Date().toISOString(),
      lvl: level,
      event,
      sessionId,
      outageId: outage?.id,
      userId: userOverride.userId ?? localStorage.getItem('userId'),
      username: userOverride.username ?? localStorage.getItem('username'),
      email: userOverride.email ?? localStorage.getItem('email'),
      role: userOverride.role ?? localStorage.getItem('userRole'),
      appVersion: localStorage.getItem('appversion'),
      buildVersion: process.env.REACT_APP_BUILD_VERSION || undefined,
      route: typeof window !== 'undefined' ? window.location.pathname : undefined,
      conn: currentConn,
      online: typeof navigator !== 'undefined' ? navigator.onLine : undefined,
      vis: typeof document !== 'undefined' ? document.visibilityState : undefined,
      tz,
      tzOffset,
      locale,
      ua,
      ...(scrub(rest) as Partial<LogEntry>)
    };
    if (extra) entry.extra = scrub(extra);

    writeQueue([...readQueue(), entry]);

    if (level === 'error') scheduleFlush(ERROR_FLUSH_DEBOUNCE_MS);
    else ensureTimer();
  } catch {
    // A logger that throws is worse than no logger.
  }
}

export const logInfo = (event: string, props?: Partial<LogEntry>) => logEvent('info', event, props);
export const logWarn = (event: string, props?: Partial<LogEntry>) => logEvent('warn', event, props);
export const logError = (event: string, props?: Partial<LogEntry>) => logEvent('error', event, props);

// ── Shipping ─────────────────────────────────────────────────────────────────
let sending = false;
let failures = 0;
let nextAttemptAt = 0;
let interval: ReturnType<typeof setInterval> | undefined;
let debounce: ReturnType<typeof setTimeout> | undefined;

const ensureTimer = () => {
  if (!remoteLoggingEnabled || interval) return;
  interval = setInterval(() => void flushLogs(), FLUSH_INTERVAL_MS);
};

const scheduleFlush = (delay: number) => {
  ensureTimer();
  if (!remoteLoggingEnabled) return;
  if (debounce) clearTimeout(debounce);
  debounce = setTimeout(() => {
    debounce = undefined;
    void flushLogs();
  }, delay);
};

const endpoint = () => (LOG_KEY ? `${LOG_URL}?k=${encodeURIComponent(LOG_KEY)}` : LOG_URL);

/**
 * Take at most one batch off the front of the queue, respecting both caps.
 * Returns the batch and the remainder; nothing is removed from storage until a
 * send is confirmed, so a failed flush leaves the queue exactly as it was.
 */
const nextBatch = (queue: LogEntry[]): { batch: LogEntry[]; rest: LogEntry[] } => {
  const batch: LogEntry[] = [];
  let bytes = 0;
  for (const entry of queue) {
    const size = JSON.stringify(entry).length + 1;
    if (batch.length >= MAX_BATCH_ENTRIES) break;
    // Always take at least one, even if a single entry is over the byte cap —
    // otherwise an oversized entry wedges the queue permanently.
    if (batch.length > 0 && bytes + size > MAX_BATCH_BYTES) break;
    batch.push(entry);
    bytes += size;
  }
  return { batch, rest: queue.slice(batch.length) };
};

/**
 * Body is sent as `text/plain` on purpose: that content type is CORS-safelisted,
 * so this is a "simple" request with NO preflight. It keeps the shared key in the
 * query string rather than a custom header (a custom header would force an
 * OPTIONS round-trip, which `sendBeacon` cannot complete during unload), and it
 * makes the beacon and fetch paths byte-identical. The sink parses JSON
 * regardless of the declared type.
 */
export async function flushLogs(): Promise<void> {
  if (!remoteLoggingEnabled || sending) return;
  if (Date.now() < nextAttemptAt) return;

  const queue = readQueue();
  if (!queue.length) return;

  sending = true;
  const { batch, rest } = nextBatch(queue);
  const dropped = peekDropped();
  try {
    const res = await fetch(endpoint(), {
      method: 'POST',
      mode: 'cors',
      cache: 'no-store',
      credentials: 'omit',
      headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
      body: JSON.stringify({ v: 1, sessionId, dropped, entries: batch })
    });
    if (!res.ok) {
      // A 4xx means the sink examined this batch and refused it — malformed, too
      // large, wrong key. Retrying byte-identical content cannot change that
      // answer, and a batch stuck at the head of the queue would wedge every
      // entry behind it forever. Drop it and move on; 408/429 are the exceptions
      // that genuinely mean "later". Everything 5xx stays queued.
      const permanent = res.status >= 400 && res.status < 500 && res.status !== 408 && res.status !== 429;
      if (permanent) {
        const rejectedIds = new Set(batch.map((e) => e.id));
        writeQueue(readQueue().filter((e) => !rejectedIds.has(e.id)));
        bumpDropped(batch.length);
      }
      throw new Error(`log sink ${res.status}`);
    }

    failures = 0;
    nextAttemptAt = 0;
    settleDropped(dropped);
    // Re-read rather than reusing `rest`: entries logged while this request was
    // in flight would otherwise be thrown away with the batch we just sent.
    const current = readQueue();
    const sentIds = new Set(batch.map((e) => e.id));
    writeQueue(current.filter((e) => !sentIds.has(e.id)));

    // More waiting? Keep going straight away rather than idling a whole interval.
    if (rest.length) scheduleFlush(250);
  } catch {
    // Unreachable sink, blocked request, 4xx/5xx — keep everything queued and
    // back off. The `dropped` counter is deliberately left untouched so a failed
    // send doesn't erase the fact that entries were discarded. If the batch DID
    // arrive and only the response was lost, the retry duplicates it; entries
    // carry a stable `id` so the reader de-dupes. Duplicated evidence beats
    // missing evidence.
    nextAttemptAt = Date.now() + SEND_BACKOFF_MS[Math.min(failures, SEND_BACKOFF_MS.length - 1)];
    failures += 1;
  } finally {
    sending = false;
  }
}

/**
 * Last-gasp send on page hide. `sendBeacon` hands the payload to the browser,
 * which delivers it after the document is gone — the only transport that
 * survives a tab closing on a spinner that never resolved.
 */
const flushOnHide = (): void => {
  if (!remoteLoggingEnabled) return;
  try {
    const queue = readQueue();
    if (!queue.length) return;
    if (typeof navigator === 'undefined' || typeof navigator.sendBeacon !== 'function') return;
    const { batch } = nextBatch(queue);
    const body = new Blob(
      [JSON.stringify({ v: 1, sessionId, unload: true, entries: batch })],
      { type: 'text/plain;charset=UTF-8' }
    );
    // `true` only means "queued for transmission", so the entries stay in the
    // queue and go out again on the next load. The reader de-dupes on `id`.
    navigator.sendBeacon(endpoint(), body);
  } catch {
    /* nothing survives here anyway */
  }
};

// ── Local inspection (support path) ───────────────────────────────────────────
// When the sink is unreachable or not yet configured, the log still exists on the
// user's machine. /status/diagnostics reads these.
export const readLocalLogs = (): LogEntry[] => readQueue();

export const clearLocalLogs = (): void => {
  try {
    localStorage.removeItem(QUEUE_KEY);
    localStorage.removeItem(META_KEY);
  } catch {
    /* ignore */
  }
};

export const downloadLocalLogs = (): void => {
  try {
    const entries = readQueue();
    const stamp = new Date().toISOString().slice(0, 10);
    const who = localStorage.getItem('userId') || localStorage.getItem('email') || 'anonymous';
    const blob = new Blob([entries.map((e) => JSON.stringify(e)).join('\n')], {
      type: 'application/x-ndjson'
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `frontend-log-${who}-${stamp}.jsonl`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 2000);
  } catch {
    /* ignore */
  }
};

// ── Boot ─────────────────────────────────────────────────────────────────────
/**
 * Called once from App. Records the page load, installs the global error hooks,
 * and — the important part — drains anything left over from a PREVIOUS load.
 * That leftover is the outage: requests that never landed, logged locally while
 * the API was dead, now shipped from a session where the network works.
 */
let initialized = false;

export function initFrontendLog(): void {
  // Idempotent: a second call must not double-register the error listeners (which
  // would duplicate every entry they produce).
  if (initialized) return;
  initialized = true;
  try {
    const pending = readQueue().length;
    // An ordinary page load is not a failure and is not recorded (MIN_LEVEL drops
    // it). A load that finds entries left over from a PREVIOUS session is worth a
    // line, though: it is the only thing that explains why failures timestamped
    // yesterday are arriving today, which otherwise looks like a clock bug.
    if (pending > 0) {
      logWarn('app_reopened_with_backlog', {
        msg: `Page load carrying ${pending} entries buffered by an earlier session — the failures below were recorded then, not now`,
        reason: 'deferred_delivery',
        extra: {
          pendingFromPreviousSession: pending,
          referrer:
            typeof document !== 'undefined' ? document.referrer || undefined : undefined,
          remoteSink: remoteLoggingEnabled
        }
      });
    }

    if (typeof window !== 'undefined') {
      // A render crash is caught by GlobalErrorBoundary; these two cover
      // everything outside the React tree.
      window.addEventListener('error', (event) => {
        logError('unhandled_error', {
          msg: String(event?.message || 'Unhandled error'),
          extra: {
            source: event?.filename,
            line: event?.lineno,
            column: event?.colno,
            stack: (event as any)?.error?.stack
          }
        });
      });
      window.addEventListener('unhandledrejection', (event: any) => {
        const reason = event?.reason;
        logError('unhandled_rejection', {
          msg: String(reason?.message || reason || 'Unhandled promise rejection'),
          extra: { stack: reason?.stack }
        });
      });
      // pagehide covers the cases beforeunload misses (mobile Safari, tab
      // discard); visibilitychange catches a backgrounded tab that is never
      // formally closed.
      window.addEventListener('pagehide', flushOnHide);
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') flushOnHide();
      });
    }

    // Drain the backlog now — this is the delivery moment for the last outage.
    scheduleFlush(1200);
  } catch {
    /* never block app start */
  }
}
